import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { SERVICES } from '../data/mock';
import { CalendlyEmbed } from '../components/CalendlyEmbed';
import { Badge } from '../components/ui/badge';
import * as Icons from 'lucide-react';

const fadeInUp = {
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: '-60px' },
  transition: { duration: 0.5, ease: 'easeOut' },
};

export default function ServicesPage() {
  return (
    <main>
      {/* Hero */}
      <section className="pt-32 pb-16 px-6" style={{ background: 'radial-gradient(at 53% 78%, hsla(60,100%,50%,0.15) 0px, transparent 50%), radial-gradient(at 71% 91%, hsla(108,100%,50%,0.15) 0px, transparent 50%)' }}>
        <div className="max-w-[800px] mx-auto text-center">
          <motion.div {...fadeInUp}>
            <Badge className="mb-5 bg-[rgba(148,242,127,0.12)] text-[rgb(13,121,22)] border-[rgba(148,242,127,0.3)] px-4 py-1.5 text-xs font-medium rounded-full">
              Full-Service Tax Solutions
            </Badge>
          </motion.div>
          <motion.h1 {...fadeInUp} transition={{ delay: 0.1 }}
            className="font-bold text-[rgb(0,55,32)] tracking-tight mb-4"
            style={{ fontSize: 'clamp(2rem, 4.5vw, 3.5rem)', lineHeight: '0.92' }}>
            Services Tailored to Your Tax Needs
          </motion.h1>
          <motion.p {...fadeInUp} transition={{ delay: 0.2 }}
            className="text-[rgb(131,146,140)] max-w-[600px] mx-auto" style={{ fontSize: 'clamp(1rem, 2vw, 1.15rem)' }}>
            From simple W-2 returns to complex business filings, we handle it all — from anywhere in the country.
          </motion.p>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-16 bg-white">
        <div className="max-w-[1200px] mx-auto px-6">
          <div className="flex flex-col gap-16">
            {SERVICES.map((service, idx) => {
              const Icon = Icons[service.icon] || Icons.FileText;
              const isEven = idx % 2 === 0;
              return (
                <motion.div key={service.id} {...fadeInUp}
                  className={`grid grid-cols-1 lg:grid-cols-2 gap-8 items-start ${isEven ? '' : 'lg:direction-rtl'}`}>
                  {/* Info */}
                  <div className={isEven ? '' : 'lg:order-2'}>
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-10 h-10 rounded-lg bg-[rgba(148,242,127,0.12)] flex items-center justify-center">
                        <Icon className="w-5 h-5 text-[rgb(13,121,22)]" />
                      </div>
                      <h2 className="font-semibold text-[rgb(0,55,32)]" style={{ fontSize: 'clamp(1.25rem, 3vw, 1.75rem)' }}>{service.title}</h2>
                    </div>
                    <p className="text-[rgb(131,146,140)] text-sm mb-4 leading-relaxed">{service.description}</p>
                    <div className="mb-4">
                      <h4 className="text-sm font-semibold text-[rgb(0,55,32)] mb-2">Who It's For</h4>
                      <p className="text-sm text-[rgb(131,146,140)] leading-relaxed">{service.whoItsFor}</p>
                    </div>
                    <div className="mb-4">
                      <h4 className="text-sm font-semibold text-[rgb(0,55,32)] mb-2">Typical Scenarios</h4>
                      <div className="flex flex-wrap gap-2">
                        {service.scenarios.map((s, i) => (
                          <Badge key={i} variant="outline" className="text-xs text-[rgb(131,146,140)] border-black/10 rounded-full font-normal">{s}</Badge>
                        ))}
                      </div>
                    </div>
                    <div className="flex items-center gap-3 mt-5">
                      <span className="text-lg font-bold text-[rgb(0,55,32)]">{service.price}</span>
                      <Link to="/book" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-gradient-to-br from-[#8FEC78] to-[#81DD67] text-white font-semibold text-sm no-underline hover:shadow-md transition-shadow">
                        Book Free Consult
                      </Link>
                    </div>
                  </div>
                  {/* Details Card */}
                  <div className={`bg-[rgba(0,0,0,0.02)] border border-black/10 rounded-xl p-6 ${isEven ? '' : 'lg:order-1'}`}>
                    <div className="mb-5">
                      <h4 className="text-sm font-semibold text-[rgb(0,55,32)] mb-3 flex items-center gap-2">
                        <Icons.CheckCircle className="w-4 h-4 text-[rgb(13,121,22)]" /> What's Included
                      </h4>
                      <ul className="space-y-2">
                        {service.whatsIncluded.map((item, i) => (
                          <li key={i} className="flex items-start gap-2 text-sm text-[rgb(14,15,12)]">
                            <Icons.Check className="w-4 h-4 text-[#8FEC78] mt-0.5 shrink-0" />
                            {item}
                          </li>
                        ))}
                      </ul>
                    </div>
                    <div>
                      <h4 className="text-sm font-semibold text-[rgb(0,55,32)] mb-3 flex items-center gap-2">
                        <Icons.FileUp className="w-4 h-4 text-[rgb(13,121,22)]" /> Documents Needed
                      </h4>
                      <ul className="space-y-2">
                        {service.docsNeeded.map((doc, i) => (
                          <li key={i} className="flex items-start gap-2 text-sm text-[rgb(131,146,140)]">
                            <Icons.File className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                            {doc}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA + Calendly */}
      <section className="py-20" style={{ background: 'rgba(0,0,0,0.02)' }}>
        <div className="max-w-[900px] mx-auto px-6">
          <motion.div {...fadeInUp} className="text-center mb-10">
            <h2 className="font-semibold text-[rgb(0,55,32)] tracking-tight mb-3" style={{ fontSize: 'clamp(1.875rem, 4vw, 2.5rem)' }}>Not Sure Which Service You Need?</h2>
            <p className="text-[rgb(131,146,140)]">Book a free consultation and we'll help you figure it out.</p>
          </motion.div>
          <motion.div {...fadeInUp} className="rounded-2xl border border-black/10 overflow-hidden bg-white shadow-sm">
            <CalendlyEmbed minimal />
          </motion.div>
        </div>
      </section>
    </main>
  );
}
